/* eslint-disable max-lines-per-function */
import { Autocomplete, AutocompleteItem, Input } from '@nextui-org/react'
import React, { useState, useCallback } from 'react'

import { useResourcesApi } from '@/services/api/resourcesApi'
import { InstitutionSap } from '@/types/forms/educationForm'

interface UniversityOption {
    value: string
    label: string
    country: string
    department: string
    population: string
    index: number
}

interface HomologationSelectorsProps {
    selectedUniversity?: string
    selectedProgram?: string
    selectedUniversityText?: string
    onUniversityChange?: (value: string) => void
    onProgramChange?: (value: string) => void
    onUniversityDataChange?: (universities: UniversityOption[]) => void
    onUniversityTextChange?: (text: string) => void
    disabled?: boolean
}

export const HomologationSelectors: React.FC<HomologationSelectorsProps> = ({
    selectedUniversity,
    selectedProgram,
    selectedUniversityText,
    onUniversityChange,
    onProgramChange,
    onUniversityDataChange,
    onUniversityTextChange,
    disabled
}) => {
    const resourcesApi = useResourcesApi()
    const [universities, setUniversities] = useState<UniversityOption[]>([])
    const [loadingUniversities, setLoadingUniversities] = useState(false)

    // Función para buscar universidades por alias
    const searchUniversities = useCallback(
        async (searchTerm: string) => {
            if (!searchTerm || searchTerm.length < 3) {
                setUniversities([])

                return
            }

            setLoadingUniversities(true)
            try {
                const response =
                    await resourcesApi.getUniversitiesByName(searchTerm)

                console.log('University search response:', response)

                // La respuesta puede venir directamente como array o dentro de response.data
                let universitiesData = []

                if (Array.isArray(response)) {
                    universitiesData = response
                } else if (
                    response &&
                    response.data &&
                    Array.isArray(response.data)
                ) {
                    universitiesData = response.data
                }

                console.log('Universities data:', universitiesData)

                if (universitiesData.length > 0) {
                    const universityOptions: UniversityOption[] =
                        universitiesData
                            .map(
                                (
                                    institution: InstitutionSap,
                                    index: number
                                ) => ({
                                    value: institution.id?.toString() || '',
                                    label: institution.denominacionObjeto || '',
                                    country: institution.denominacionPais || '',
                                    department:
                                        institution.denominacionDepartamento ||
                                        '',
                                    population: institution.poblacion || '',
                                    index: index
                                })
                            )
                            .filter(
                                (option: UniversityOption) =>
                                    option.value && option.label
                            )

                    console.log('University options mapped:', universityOptions)
                    setUniversities(universityOptions)

                    // Notificar al componente padre sobre los datos de universidades
                    if (onUniversityDataChange) {
                        onUniversityDataChange(universityOptions)
                    }
                } else {
                    console.log('No valid data in response')
                    setUniversities([])
                }
            } catch (error) {
                console.error('Error searching universities:', error)
                setUniversities([])
            } finally {
                setLoadingUniversities(false)
            }
        },
        [resourcesApi]
    )

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Dropdown para seleccionar universidad */}
            <div id="tour-university-select">
                <Autocomplete
                    label="Seleccione la universidad"
                    placeholder="Escribe para buscar universidad..."
                    className="w-full"
                    isLoading={loadingUniversities}
                    items={universities}
                    selectedKey={selectedUniversity}
                    inputValue={selectedUniversityText || ''}
                    isDisabled={Boolean(disabled)}
                    classNames={{
                        popoverContent: 'w-full',
                        // En NextUI el scroll suele vivir en el wrapper/ScrollShadow,
                        // no en el listbox directo (por eso aquí forzamos el wrapper).
                        listboxWrapper:
                            'max-h-[360px] overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin] [scrollbar-color:rgb(156_163_175)_transparent] [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-400/70 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-500/80',
                        listbox: 'overflow-visible'
                    }}
                    listboxProps={{
                        className: 'overflow-visible'
                    }}
                    showScrollIndicators
                    scrollShadowProps={{
                        className: 'max-h-[360px]'
                    }}
                    onSelectionChange={(key) => {
                        const value = key as string
                        const selectedUniv = universities.find(
                            (u) => u.value === value
                        )

                        onUniversityChange?.(value)
                        if (selectedUniv) {
                            onUniversityTextChange?.(selectedUniv.label)
                        }
                    }}
                    onInputChange={(value) => {
                        console.log('Input changed:', value)
                        onUniversityTextChange?.(value)
                        searchUniversities(value)
                    }}
                    variant="bordered"
                    size="lg"
                    allowsCustomValue={false}
                    menuTrigger="input"
                >
                    {(university) => (
                        <AutocompleteItem
                            key={university.value}
                            value={university.value}
                            textValue={university.label}
                            className="min-h-[40px]"
                        >
                            <div className="flex flex-col gap-1">
                                <span className="font-medium text-gray-900 truncate">
                                    {university.label}
                                </span>
                                <div className="flex items-center gap-2 text-xs flex-wrap">
                                    {university.country && (
                                        <span className="px-2 py-0.5 rounded-md bg-[#dfdf3f] text-black truncate max-w-[40%]">
                                            {university.country}
                                        </span>
                                    )}
                                    {university.department && (
                                        <span className="px-2 py-0.5 rounded-md bg-[#00c0b3] text-white truncate max-w-[40%]">
                                            {university.department}
                                        </span>
                                    )}
                                    {university.population && (
                                        <span className="px-2 py-0.5 rounded-md bg-[#a855f7] text-white truncate max-w-[40%]">
                                            {university.population}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </AutocompleteItem>
                    )}
                </Autocomplete>
            </div>

            {/* Input para programa de destino */}
            <div id="tour-program-input">
                <Input
                    label="Programa de origen"
                    placeholder="Digita el programa de origen de la Universidad"
                    className="w-full"
                    value={selectedProgram || ''}
                    isDisabled={Boolean(disabled)}
                    onValueChange={(value) => {
                        onProgramChange?.(value)
                    }}
                    variant="bordered"
                    size="lg"
                    description={
                        <span>
                            <b>Importante:</b> el nombre debe coincidir{' '}
                            <b>textualmente</b> con el que figura en tu
                            certificado.
                        </span>
                    }
                />
            </div>
        </div>
    )
}
